import { useMemo, useState } from 'react';

import { useAtomValue } from 'jotai/react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useTranslate } from '@i18n';
import { useCategories, useKeyParameterUpdate, useUiNodes, useUiParameters } from '@queries';
import { activeSwingIdAtom, activityAtom, layoutKeyParameterIdAtom, useEnsureAtomValue } from '@store';
import { Button, colors, Heading3, Inline, LegoIcon, Modal, Stack, u } from '@ui';

import { NewParameter } from '../../NewParameter/NewParameter';
import { filterParameters, ParameterFilter, ParameterFilterProvider } from '../../ParameterFilter';

import type { ParameterFilterFormValues } from '../../ParameterFilter/ParameterFilterProvider';

interface KeyParameterEditorProps {
    onClose: () => void;
}

const P_POSITION_REGEX = /p\d{1,2}$/;

type ParameterGroupsProps = {
    groupId: string;
    groupTitle: string;
    selectedParameterId: string;
    onSelect: (parameterId: string) => void;
    activeSwingId?: string;
    activityId: number;
};

const ParameterGroups = ({
    groupId,
    groupTitle,
    selectedParameterId,
    onSelect,
    activityId,
    activeSwingId,
}: ParameterGroupsProps) => {
    const { control } = useFormContext<ParameterFilterFormValues>();
    const filters = useWatch({ control, name: 'filters' });
    const positions = useWatch({ control, name: 'positions' });
    const { data: parameters = [] } = useUiParameters();
    const { data: swingFoundationGroups = [] } = useCategories('swing_foundation_group');

    const position = groupId.match(P_POSITION_REGEX)?.[0];
    const filteredSwingFoundationGroups = useMemo(
        () => swingFoundationGroups.filter((group) => position && group.id.includes(position)),
        [position, swingFoundationGroups],
    );

    const parameterGroupsMap = useMemo(() => {
        return Object.fromEntries(
            filteredSwingFoundationGroups.map(
                (group) =>
                    [
                        group.id,
                        filterParameters(
                            parameters.filter((parameter) => parameter.categories.swingFoundationGroup === group.id),
                            filters,
                            positions,
                        ),
                    ] as const,
            ),
        );
    }, [filteredSwingFoundationGroups, filters, parameters, positions]);

    const visibleSwingFoundationGroups = useMemo(
        () => filteredSwingFoundationGroups.filter((group) => parameterGroupsMap[group.id]?.length > 0),
        [filteredSwingFoundationGroups, parameterGroupsMap],
    );
    const t = useTranslate();

    if (visibleSwingFoundationGroups.length === 0) return null;

    return (
        <Stack gap={u(2)} p={u(2)} radius="md" bg="panel">
            <Stack justifyContent="space-between">
                <Inline alignItems="center" py={u(1)}>
                    <LegoIcon size={24} color={colors.blue[600]} />
                    <Heading3>{groupTitle}</Heading3>
                </Inline>
            </Stack>

            {visibleSwingFoundationGroups.map((group) => (
                <Stack gap={u(2)} key={group?.id || 'other'}>
                    <Heading3>{t(group?.i18n ?? 'Other')}</Heading3>

                    <Inline flexWrap="wrap" columnGap={u(2)}>
                        {parameterGroupsMap[group.id]?.map((parameter) => (
                            <NewParameter
                                key={parameter.id}
                                node={parameter}
                                isSelected={selectedParameterId === parameter.id}
                                onClick={() => onSelect(parameter.id)}
                                activeSwingId={activeSwingId}
                                activityId={activityId}
                            />
                        ))}
                    </Inline>
                </Stack>
            ))}
        </Stack>
    );
};

export function KeyParameterEditor({ onClose }: KeyParameterEditorProps) {
    const { data: swingFoundationGroups = [] } = useUiNodes('swing_foundations');
    const activity = useEnsureAtomValue(activityAtom);
    const { mutateAsync: updateKeyParameter } = useKeyParameterUpdate(activity.layoutId ?? -1);
    const keyParameter = useAtomValue(layoutKeyParameterIdAtom);
    const activeSwingId = useAtomValue(activeSwingIdAtom);

    const [selectedParameter, setSelectedParameter] = useState(keyParameter);

    const parameterGroups = useMemo(
        () => swingFoundationGroups.filter((group) => group.id !== 'swing_foundations.default_kiosk'),
        [swingFoundationGroups],
    );

    const t = useTranslate();
    const handleSave = async () => {
        updateKeyParameter(selectedParameter);
        onClose();
    };

    return (
        <Modal
            open
            title="Edit Key Parameter"
            onDismiss={onClose}
            footer={{
                primary: (
                    <Button variant="primary" onClick={handleSave}>
                        Save
                    </Button>
                ),
                secondary: (
                    <Button variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                ),
            }}
        >
            <ParameterFilterProvider>
                <ParameterFilter />
                {parameterGroups?.map((swingFoundationGroup) => {
                    return (
                        <ParameterGroups
                            key={swingFoundationGroup.id}
                            groupId={swingFoundationGroup.id}
                            groupTitle={t(swingFoundationGroup.i18n ?? '-')}
                            onSelect={setSelectedParameter}
                            selectedParameterId={selectedParameter}
                            activeSwingId={activeSwingId}
                            activityId={activity.id}
                        />
                    );
                })}
            </ParameterFilterProvider>
        </Modal>
    );
}
